/**
 * Painel lateral do editor: **o bloco selecionado, e nada mais**.
 *
 * Antes ele acumulava oito abas — Bloco, Estilo, Domínios, Segredos, Instalar, Análise, Leads,
 * Limite — em 384px. Três coisas de naturezas diferentes no mesmo lugar: o objeto selecionado no
 * canvas, a configuração do webchat inteiro e os números observados. Os sintomas eram todos
 * consequência disso:
 *
 *  - as abas quebravam em duas linhas e ainda assim os rótulos precisaram ser encurtados
 *    ("Aparência" virou "Estilo", "Instalação" virou "Instalar") para caber;
 *  - clicar num bloco estando em Domínios escondia o formulário dele, o que exigiu um `select()`
 *    que forçava a aba de volta — tratamento de sintoma;
 *  - a tabela de análise e a lista de contatos viviam espremidas numa coluna em que não cabem.
 *
 * Hoje configuração é `/w/:id/config` e números são `/w/:id/dados`, cada um em tela cheia. Aqui
 * sobra a única coisa que **precisa** ficar ao lado do canvas, porque só faz sentido junto dele.
 *
 * O rodapé com as ações destrutivas é fixo: numa condição com oito regras o formulário rola muito, e
 * "Excluir bloco" ficava a uma tela de distância de onde se estava editando.
 */

import type {
  BackendWebhookBlockData,
  BlockData,
  BlockKind,
  BrowserCodeBlockData,
  BrowserWebhookBlockData,
  ButtonsBlockData,
  ConditionBlockData,
  InputBlockData,
  MessageBlockData,
} from '@merlin/flow-engine';
import { useEditor } from '../../store/editor.js';
import { BLOCK_META, blockChip, type AnyBlockNode } from '../../flow/mapping.js';
import { BLOCK_ICONS, IconeDuplicar, IconeFluxo, IconeLixeira } from '../icons.js';
import { Badge, Button, EmptyState } from '../ui.js';
import {
  BackendWebhookForm,
  BrowserCodeForm,
  BrowserWebhookForm,
  ButtonsForm,
  ConditionForm,
  InputForm,
  MessageForm,
} from './BlockForms.js';

function BlockBody({ node }: { node: AnyBlockNode }) {
  const updateBlock = useEditor((state) => state.updateBlock);
  const removeButtonOption = useEditor((state) => state.removeButtonOption);
  const secretNames = useEditor((state) => state.secretNames);
  const outboundAllowlist = useEditor((state) => state.outboundAllowlist);

  const type = node.type as BlockKind;
  const update = (patch: Partial<BlockData>): void => updateBlock(node.id, patch);

  if (type === 'message') return <MessageForm data={node.data.block as MessageBlockData} update={update} />;
  if (type === 'input') return <InputForm data={node.data.block as InputBlockData} update={update} />;
  if (type === 'buttons') {
    return (
      <ButtonsForm
        data={node.data.block as ButtonsBlockData}
        update={update}
        // Pelo store, porque remover a opção precisa levar a aresta que saía dela.
        onRemoveOption={(optionId) => removeButtonOption(node.id, optionId)}
      />
    );
  }
  if (type === 'condition') return <ConditionForm data={node.data.block as ConditionBlockData} update={update} />;
  if (type === 'browserCode') {
    return <BrowserCodeForm data={node.data.block as BrowserCodeBlockData} update={update} />;
  }
  if (type === 'browserWebhook') {
    return <BrowserWebhookForm data={node.data.block as BrowserWebhookBlockData} update={update} />;
  }
  return (
    <BackendWebhookForm
      data={node.data.block as BackendWebhookBlockData}
      update={update}
      secretNames={secretNames}
      outboundAllowlist={outboundAllowlist}
    />
  );
}

export function Inspector() {
  const selectedNodeId = useEditor((state) => state.selectedNodeId);
  const nodes = useEditor((state) => state.nodes);
  const removeNode = useEditor((state) => state.removeNode);
  const duplicateNode = useEditor((state) => state.duplicateNode);
  const setStartNode = useEditor((state) => state.setStartNode);
  const startNodeId = useEditor((state) => state.startNodeId);

  const node = nodes.find((candidate) => candidate.id === selectedNodeId) ?? null;

  if (!node) {
    return (
      <div className="flex h-full items-center bg-white">
        <EmptyState
          icon={IconeFluxo}
          title="Nenhum bloco selecionado"
          description="Clique num bloco do canvas para editar o conteúdo dele aqui, ou arraste um novo da paleta à esquerda."
        />
      </div>
    );
  }

  const type = node.type as BlockKind;
  const meta = BLOCK_META[type];
  const Icone = BLOCK_ICONS[type];
  const ehInicio = startNodeId === node.id;

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Mesmo ícone e mesma cor do bloco no canvas e na paleta — o mesmo objeto, os três lugares. */}
      <header className="flex items-start gap-3 border-b border-slate-200 px-4 py-3">
        <span className="flex h-6 shrink-0 items-center">
          <span className={`grid size-8 place-items-center rounded-lg ${blockChip(type)}`}>
            <Icone size={17} />
          </span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-base leading-6 font-semibold text-slate-900">{meta.label}</p>
          <p className="text-sm leading-snug text-slate-500">{meta.description}</p>
        </div>
        {ehInicio ? <Badge tone="ok">início</Badge> : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <BlockBody node={node} />
      </div>

      {/*
        `ml-auto` no destrutivo: ele fica do outro lado do rodapé, separado das ações que se usam todo
        dia. Um "Excluir bloco" colado no "Duplicar" é convite a errar o alvo.
      */}
      <footer className="flex flex-wrap items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
        <Button onClick={() => duplicateNode(node.id)}>
          <IconeDuplicar />
          Duplicar
        </Button>
        {ehInicio ? null : <Button onClick={() => setStartNode(node.id)}>Definir como início</Button>}
        <Button variant="danger" className="ml-auto" onClick={() => removeNode(node.id)}>
          <IconeLixeira />
          Excluir bloco
        </Button>
      </footer>
    </div>
  );
}
